import { useCallback } from "react";
import { supabase } from "../../lib/supabase";
import {
  ApprovalComment,
  ApprovalCommentFormData,
} from "../../components/approvalsCommentsTypes";

export const useApprovalComments = () => {
  const fetchComments = useCallback(
    async (approvalId: string): Promise<ApprovalComment[]> => {
      try {
        const { data, error } = await supabase
          .from("approval_comments")
          .select(
            `
            id,
            approval_id,
            author_id,
            author_role,
            text,
            created_at,
            users!approval_comments_author_id_fkey (
              name,
              email
            )
          `
          )
          .eq("approval_id", approvalId)
          .order("created_at", { ascending: true });

        if (error) {
          console.error("Error fetching comments:", error);
          return [];
        }

        // Map user data to comment
        const transformedData: ApprovalComment[] = (data || []).map(
          (item: any) => ({
            ...item,
            author_name: item.users?.name,
            author_email: item.users?.email,
          })
        );

        return transformedData;
      } catch (err) {
        console.error("Error in fetchComments:", err);
        return [];
      }
    },
    []
  );

  const addComment = useCallback(
    async (
      approvalId: string,
      userId: string,
      formData: ApprovalCommentFormData,
      authorRole: "contractor" | "client"
    ): Promise<ApprovalComment | null> => {
      try {
        const comment = {
          approval_id: approvalId,
          author_id: userId,
          author_role: authorRole,
          text: formData.text,
          created_at: new Date().toISOString(),
        };

        const { data, error } = await supabase
          .from("approval_comments")
          .insert([comment])
          .select(
            `
            id,
            approval_id,
            author_id,
            author_role,
            text,
            created_at,
            users!approval_comments_author_id_fkey (
              name,
              email
            )
          `
          );

        if (error) {
          console.error("Error adding comment:", error);
          return null;
        }

        if (!data || data.length === 0) {
          console.error("No data returned after comment insert");
          return null;
        }

        const commentData = data[0] as any;
        const transformedComment: ApprovalComment = {
          id: commentData.id,
          approval_id: commentData.approval_id,
          author_id: commentData.author_id,
          author_role: commentData.author_role,
          text: commentData.text,
          created_at: commentData.created_at,
          author_name: commentData.users?.name || "",
          author_email: commentData.users?.email || "",
        };

        return transformedComment;
      } catch (err) {
        console.error("Error in addComment:", err);
        return null;
      }
    },
    []
  );

  return {
    fetchComments,
    addComment,
  };
};
